/**
 * "Which store is this URL about?" — the pure half of the store-in-the-URL
 * design, with no React and no tRPC in it.
 *
 * The store code is part of the route, so these helpers are what turn a pathname
 * into a store. Building the URLs is the other half and lives in `./routes`,
 * which owns every route string; this module only reads them back. They are
 * typed structurally (`RoutableStore`) rather than against the router's
 * `StoreItem`: that keeps this module free of any dependency on the tRPC client,
 * and the generic signatures still hand the caller back its own store type.
 */
import { STORE_PAGES } from './routes'
import type { StorePage } from './routes'

const STORE_PAGE_SEGMENTS = new Set<string>(STORE_PAGES)

const STORAGE_KEY = 'retail:activeStoreId'

/** What these helpers need from a store — `StoreItem` satisfies it. */
type RoutableStore = { id: number; code: string }

/** The store's URL segment: its `code` lowercased (every store has a code). */
export const storeCodeSegment = (store: RoutableStore): string =>
  store.code.toLowerCase()

/**
 * Resolve one store from a URL segment, scoped to the user's stores. Matches the
 * `code` case-insensitively (codes are unique within a client); a bare numeric
 * segment also resolves by id, defensively. Returns undefined when nothing
 * matches (unknown code, or a store the user has no membership for — store.list
 * only returns the user's stores).
 */
export const resolveStoreByCode = <TStore extends RoutableStore>(
  stores: Array<TStore>,
  segment: string | undefined,
): TStore | undefined => {
  if (segment === undefined) return undefined
  const lower = segment.toLowerCase()
  const byCode = stores.find((store) => store.code.toLowerCase() === lower)
  if (byCode !== undefined) return byCode
  const asId = Number(segment)
  if (Number.isInteger(asId)) return stores.find((store) => store.id === asId)
  return undefined
}

/**
 * Split a store-scoped pathname into its page + storeCode segments. Every page
 * puts the store code at segment index 1 (the page, the store's code, and for
 * the order detail an extra id), so one parser serves them all.
 */
export const parseStoreRoute = (
  pathname: string,
): { page: StorePage | undefined; storeCode: string | undefined } => {
  const [page, storeCode] = pathname.split('/').filter(Boolean)
  return {
    page: STORE_PAGE_SEGMENTS.has(page ?? '') ? (page as StorePage) : undefined,
    storeCode,
  }
}

/** The store to land on when the URL has no (valid) code: last-used, else first. */
export const pickDefaultStore = <TStore extends RoutableStore>(
  stores: Array<TStore>,
  lastUsedId: number | undefined,
): TStore | undefined => {
  if (stores.length === 0) return undefined
  const lastUsed = lastUsedId !== undefined ? stores.find((store) => store.id === lastUsedId) : undefined
  return lastUsed ?? stores[0]
}

/** The last store the user viewed (persisted), for the `/` → store redirect. */
export const readLastUsedStoreId = (): number | undefined => {
  let raw: string | null
  try {
    raw = localStorage.getItem(STORAGE_KEY)
  } catch {
    // Storage can be walled off entirely (old Safari private mode, enterprise
    // policy, sandboxed iframe) and then getItem THROWS rather than returning
    // null. Remembering the last store is a nicety — degrade to "nothing
    // remembered", never to an exception.
    return undefined
  }
  if (raw === null) return undefined
  const parsed = Number(raw)
  return Number.isInteger(parsed) ? parsed : undefined
}

/** Records the store in view, so `/` can redirect back to it next time. */
export const rememberLastUsedStoreId = (storeId: number): void => {
  try {
    localStorage.setItem(STORAGE_KEY, String(storeId))
  } catch {
    // Same walled-off storages as the read, plus quota. This runs inside
    // StoreProvider's effect, where a throw reaches the Error Boundary and
    // blanks the whole authenticated area — a forgotten redirect target is
    // the far better failure.
  }
}
